import { useState } from "react";
import { supabase } from "@/lib/supabaseClient";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Settings, Copy, Eye, EyeOff, Loader2, CreditCard, Search } from "lucide-react";

export default function AdminConfiguracoes() {
  const publicKey = import.meta.env.VITE_MERCADOPAGO_PUBLIC_KEY || "";
  const [showPublicKey, setShowPublicKey] = useState(false);
  const [accessToken, setAccessToken] = useState("");
  const [saving, setSaving] = useState(false);

  const [firecrawlKey, setFirecrawlKey] = useState("");
  const [savingFirecrawl, setSavingFirecrawl] = useState(false);

  const masked = (val: string) => val ? val.slice(0, 12) + "••••••••" + val.slice(-4) : "Não configurada";

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success("Copiado!");
  };

  const handleUpdateAccessToken = async () => {
    if (!accessToken.trim()) {
      toast.error("Informe o Access Token.");
      return;
    }
    setSaving(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Não autenticado");

      const res = await supabase.functions.invoke("admin-update-secret", {
        body: { secret_name: "MERCADOPAGO_ACCESS_TOKEN", secret_value: accessToken.trim() },
      });

      if (res.error) throw res.error;
      toast.success("Access Token atualizado com sucesso!");
      setAccessToken("");
    } catch (err: any) {
      toast.error(err.message || "Erro ao atualizar secret.");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateFirecrawlKey = async () => {
    if (!firecrawlKey.trim()) {
      toast.error("Informe a API Key do Firecrawl.");
      return;
    }
    setSavingFirecrawl(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Não autenticado");

      const res = await supabase.functions.invoke("admin-update-secret", {
        body: { secret_name: "FIRECRAWL_API_KEY", secret_value: firecrawlKey.trim() },
      });

      if (res.error) throw res.error;
      toast.success("Firecrawl API Key atualizada com sucesso!");
      setFirecrawlKey("");
    } catch (err: any) {
      toast.error(err.message || "Erro ao atualizar secret.");
    } finally {
      setSavingFirecrawl(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Settings className="h-6 w-6" /> Configurações
        </h1>
        <p className="text-muted-foreground">Gerencie as credenciais e integrações do sistema.</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" /> Mercado Pago
          </CardTitle>
          <CardDescription>Credenciais para processamento de pagamentos.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Public Key */}
          <div className="space-y-2">
            <Label>Public Key (Frontend)</Label>
            <div className="flex gap-2">
              <Input
                readOnly
                value={showPublicKey ? publicKey : masked(publicKey)}
                className="font-mono text-sm"
              />
              <Button variant="outline" size="icon" onClick={() => setShowPublicKey(!showPublicKey)}>
                {showPublicKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
              <Button variant="outline" size="icon" onClick={() => copyToClipboard(publicKey)}>
                <Copy className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Variável <code>VITE_MERCADOPAGO_PUBLIC_KEY</code> — usada no SDK do frontend.
            </p>
          </div>

          {/* Access Token */}
          <div className="space-y-2">
            <Label>Access Token (Backend)</Label>
            <div className="flex gap-2">
              <Input
                type="password"
                placeholder="Cole o novo Access Token aqui..."
                value={accessToken}
                onChange={(e) => setAccessToken(e.target.value)}
                className="font-mono text-sm"
              />
              <Button onClick={handleUpdateAccessToken} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Atualizar"}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Secret <code>MERCADOPAGO_ACCESS_TOKEN</code> — armazenado com segurança no Supabase Vault.
              Por segurança, o valor atual não é exibido.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="h-5 w-5" /> Firecrawl (Busca OLX)
          </CardTitle>
          <CardDescription>API Key para busca de preços de mercado via Firecrawl.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <Label>API Key</Label>
          <div className="flex gap-2">
            <Input
              type="password"
              placeholder="Cole a API Key do Firecrawl aqui..."
              value={firecrawlKey}
              onChange={(e) => setFirecrawlKey(e.target.value)}
              className="font-mono text-sm"
            />
            <Button onClick={handleUpdateFirecrawlKey} disabled={savingFirecrawl}>
              {savingFirecrawl ? <Loader2 className="h-4 w-4 animate-spin" /> : "Atualizar"}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Secret <code>FIRECRAWL_API_KEY</code> — usada na Edge Function para pesquisar anúncios OLX e preços de peças.
            Obtenha em <a href="https://firecrawl.dev" target="_blank" rel="noopener noreferrer" className="text-primary underline">firecrawl.dev</a>.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

